import { Button, Flex, FormStatus, P, Tag } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import { Field, Form, Value } from '@dnb/eufemia/extensions/forms';
import type { AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import { ClientDto } from '@portals/shared/portal/ClientDto';
import { UpdateOAuthClientRequest } from '@portals/shared/portal/OAuthClientDto';
import { TeamDetailsDto } from '@portals/shared/portal/TeamDto';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { updateClient } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import useAuth from '@/hooks/useAuth';
import useReturnTo from '@/hooks/useReturnTo';
import { useUser } from '@/hooks/useUser';

import { privilegeGroup } from '../../../Credential/components/ClientCard';
import { CrqField } from '../CrqField';
import { ApiScopeEntitlementCard } from './components/ApiScopeEntitlementCard/ApiScopeEntitlementCard';
import SubmitConfirmationSummary from './components/SubmitConfirmationSummary';

import { GridContainer, GridLabel, GridValue } from '../../index.styles';

export type FormData = {
  environment: string;
  jwks: string;
  name: string;
  description: string;
  crq?: string;
  selectedScopes: Record<string, boolean>;
};

const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required for updating a client in the live environment. Enter the ID for the relevant CRQ.',
};

function getScopesFromClientAndApp(
  client: ClientDto,
  app: AppWithEntitlementsDto,
): {
  initialScopes: string[];
  initialExtraScopes: string[];
} {
  const allAppEntitlements = app?.currentApiAndScopeEntitlements.flatMap((i) => i.scopeEntitlements) ?? [];
  const entitlementsInEnvironment = new Set(
    allAppEntitlements.filter((entitlement) => entitlement.liveMode === client.liveMode).map((i) => i.scopeName),
  );

  const allClientScopes = client?.scopes ?? [];
  const initialScopes = allClientScopes.filter((scope) => entitlementsInEnvironment.has(scope));
  const initialExtraScopes = allClientScopes.filter((scope) => !initialScopes.includes(scope));
  return { initialScopes, initialExtraScopes };
}

export default function UpdateClientForm() {
  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [initialClientData, setInitialClientData] = useState<ClientDto>();
  const [initialScopes, setInitialScopes] = useState<string[]>();
  const [initialExtraScopes, setInitialExtraScopes] = useState<string[]>([]);

  const [returnTo, , clearReturnTo] = useReturnTo();
  const { teamId, appId, clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { roles } = useAuth();

  const clientFormId = `create-client-form-${useId()}`;
  const { update } = Form.setData(clientFormId);

  const { data: app, isValidating: appLoading } = useSWR<AppWithEntitlementsDto>(`/apps/${appId}`);

  const { data: team, isValidating: teamLoading } = useSWR<TeamDetailsDto>(teamId ? `/team/${teamId}` : null);

  const {
    data: client,
    isValidating: clientLoading,
    mutate: mutateClient,
  } = useSWR<ClientDto>(appId && clientId ? `/apps/${appId}/clients/${clientId}` : null);

  const hasClientManagementGrp = useMemo(() => {
    return roles.includes(privilegeGroup.id);
  }, [roles]);

  const isAdmin = useMemo(() => {
    if (app && app.owner.type !== 'team') {
      return true;
    }
    return Boolean(team && team.members.some((member) => member.email === user?.email && member.isAdmin));
  }, [team, app, user]);

  const hasPrivilegeToEditClient = useMemo(() => {
    return isAdmin && hasClientManagementGrp;
  }, [isAdmin, hasClientManagementGrp]);

  const updateFormData = useCallback(
    (path: string, value: unknown | null) => {
      update(path, value);
    },
    [update],
  );

  useEffect(() => {
    if (!clientLoading && client && !initialClientData) {
      try {
        setInitialClientData(client);
        updateFormData('/name', client.name);
        updateFormData('/description', client.description);
        updateFormData('/environment', client.liveMode ? 'live' : 'test');
      } catch (error) {
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else {
          setErrorMessage('An unexpected error occurred');
        }
      }
    }
  }, [client, clientLoading, updateFormData, initialClientData]);

  React.useEffect(() => {
    if (client && app && !initialScopes) {
      const { initialScopes, initialExtraScopes } = getScopesFromClientAndApp(client, app);

      const allCheckedScopes = client.scopes?.reduce(
        (acc, scope) => {
          acc[scope] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      updateFormData('/selectedScopes', allCheckedScopes);
      setInitialScopes(initialScopes);
      setInitialExtraScopes(initialExtraScopes);
    }
  }, [clientId, app, client, updateFormData, initialScopes]);

  const handleBack = useCallback(() => {
    if (returnTo) {
      navigate(-1);
      clearReturnTo();
      return;
    }
    navigate('/');
    clearReturnTo();
  }, [returnTo, navigate, clearReturnTo]);

  const onSubmit = useCallback(
    async (data: FormData): Promise<void | Error> => {
      if (!appId) return new Error('Application ID is missing');
      try {
        setSubmitting(true);
        const selectedScopes = Object.entries(data.selectedScopes)
          .filter(([, value]) => value)
          .map(([key]) => key);
        const requestData: UpdateOAuthClientRequest = {
          name: data.name,
          description: data.description,
          scopes: selectedScopes,
        };

        const crq = data.environment === 'live' ? data.crq : undefined;
        await updateClient(appId, clientId!, requestData, crq);
        mutateClient();
        handleBack();
      } catch (error) {
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [appId, clientId, handleBack, mutateClient],
  );

  return (
    <ModalPage
      skeleton={appLoading || clientLoading || teamLoading}
      subtitle={
        <GridContainer>
          <GridLabel>App name</GridLabel>
          <GridValue>{app?.name}</GridValue>
          <GridLabel>Description</GridLabel>
          <GridValue>{app?.description ?? ''}</GridValue>
          <GridLabel>Client ID</GridLabel>
          <GridValue>{client?.id}</GridValue>
          <GridLabel>CIAM application ID</GridLabel>
          <GridValue>{client?.ciamAppId}</GridValue>
        </GridContainer>
      }
      title={hasPrivilegeToEditClient ? 'Update client' : 'Client details'}
    >
      <Form.Handler<FormData>
        defaultData={{
          environment: '',
          jwks: '',
          name: '',
          description: '',
          crq: undefined,
          selectedScopes: {},
        }}
        disabled={isSubmitting || !hasPrivilegeToEditClient}
        id={clientFormId}
        locale="en-GB"
        onSubmit={onSubmit}
      >
        {isSubmitting && <LoadingModal />}
        <Flex.Stack gap="medium">
          <Form.Card outset={false}>
            <Value.Composition>
              <P>Environment</P>
              <Tag>{initialClientData?.liveMode ? 'LIVE' : 'TEST'}</Tag>
            </Value.Composition>
            <Form.Visibility
              animate
              visibleWhen={{
                path: '/environment',
                hasValue: 'live',
              }}
            >
              <CrqField
                customHelp={crqHelp}
                labelDescription="All changes to clients in live environment require a valid CRQ."
                path="/crq"
              />
            </Form.Visibility>
          </Form.Card>
          <Form.Card outset={false}>
            <Form.Section containerMode="view">
              <Field.String
                help={{
                  content: 'This will help to identify the client.',
                }}
                label="Client name"
                maxLength={255}
                minLength={3}
                path="/name"
                placeholder="Client name"
                required
                size="medium"
                width="stretch"
              />
              <Field.String
                label="Client description"
                maxLength={500}
                minLength={3}
                path="/description"
                placeholder="Client description"
                required
                size="medium"
                top="small"
                width="stretch"
              />
            </Form.Section>
          </Form.Card>

          <ApiScopeEntitlementCard
            availableApiAndScopeEntitlements={app?.currentApiAndScopeEntitlements ?? []}
            extraScopesInClient={initialExtraScopes}
            isLiveClient={initialClientData?.liveMode ?? false}
          />
          <Form.Visibility animate visible={!!errorMessage}>
            <FormStatus state="error" text={errorMessage} />
          </Form.Visibility>

          <Form.Visibility animate visible={hasPrivilegeToEditClient}>
            <Form.ButtonRow>
              <Button
                disabled={false}
                on_click={handleBack}
                size="large"
                text="Cancel update"
                type="button"
                variant="secondary"
              />
              <Form.SubmitButton id={clientFormId} size="large" text="Update client" />
            </Form.ButtonRow>
          </Form.Visibility>
          <Form.SubmitConfirmation
            preventSubmitWhen={() => true}
            renderWithState={({ connectWithDialog }) => {
              return (
                <SubmitConfirmationSummary
                  allEntitlements={app?.currentApiAndScopeEntitlements ?? []}
                  connectWithDialog={connectWithDialog}
                  formId={clientFormId}
                  initalExtraScopes={initialExtraScopes}
                  initialClientData={initialClientData}
                  initialScopes={initialScopes ?? []}
                  isLiveMode={initialClientData?.liveMode ?? false}
                />
              );
            }}
          />
        </Flex.Stack>
      </Form.Handler>
    </ModalPage>
  );
}
